import { createContext, useContext, useState, useEffect, useCallback } from "react";

// Sprachumschalter für den Meta-App-Review-Screencast (Aufgabe 1, 24.08.2026).
// Bewusst OHNE i18n-Bibliothek und OHNE Schlüssel-Wörterbuch: t("Deutsch", "English")
// trägt beide Fassungen direkt am Aufrufort — nichts kann aus einer separaten
// Übersetzungsdatei "fehlen", und mehrere Dateien lassen sich unabhängig voneinander
// übersetzen, ohne sich eine gemeinsame Datei zu teilen.

const LANG_KEY = "ff_ui_lang";

function detectInitialLanguage() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("lang");
    if (fromQuery === "en" || fromQuery === "de") return fromQuery;
  } catch {
    // URLSearchParams/window nicht verfügbar (SSR/Test) — nächste Quelle versuchen.
  }
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === "en" || stored === "de") return stored;
  } catch {
    // localStorage blockiert (privater Modus) — Default greift.
  }
  return "de";
}

const LanguageContext = createContext({
  language: "de",
  setLanguage: () => {},
  t: (de) => de,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(detectInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
    try {
      localStorage.setItem(LANG_KEY, language);
    } catch {
      // kein Speicherzugriff — Umschaltung bleibt für diese Sitzung trotzdem aktiv.
    }
  }, [language]);

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang === "en" ? "en" : "de");
  }, []);

  // t(deutsch, englisch): Fallback auf Deutsch, wenn keine englische Fassung übergeben
  // wurde — ein vergessener zweiter Parameter zeigt also weiterhin Text, nie eine Lücke.
  const t = useCallback(
    (de, en) => (language === "en" ? (en ?? de) : de),
    [language],
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
